import { registerPlugin, Capacitor } from '@capacitor/core';

const Review = registerPlugin('Review');
const APP_STORE_ID = '6761525480';

const KEYS = {
  openCount: 'worthly_app_open_count',
  nativePromptShown: 'worthly_native_review_prompt_at',
  fallbackShown: 'worthly_custom_review_fallback_shown',
  userDeclined: 'worthly_review_declined',
  sessionCounted: 'worthly_review_session_counted',
  reaskStart: 'worthly_review_reask_2026_09_start',
  reaskShown: 'worthly_review_reask_2026_09_shown',
};

// One-time re-ask: everyone whose first review ask happened before this update
// sees the pop-up once more, even if they tapped "Not now" last time.
const REASK_CUTOFF = Date.UTC(2026, 8, 27); // 27 Sept 2026

export const reviewService = {
  incrementOpenCountOncePerSession() {
    if (sessionStorage.getItem(KEYS.sessionCounted) === 'true') return this.getOpenCount();
    const current = parseInt(localStorage.getItem(KEYS.openCount) || '0', 10);
    const next = current + 1;
    localStorage.setItem(KEYS.openCount, String(next));
    sessionStorage.setItem(KEYS.sessionCounted, 'true');
    return next;
  },

  getOpenCount() {
    return parseInt(localStorage.getItem(KEYS.openCount) || '0', 10);
  },

  hasShownNativePrompt() {
    return !!localStorage.getItem(KEYS.nativePromptShown);
  },

  hasShownFallback() {
    return localStorage.getItem(KEYS.fallbackShown) === 'true';
  },

  hasUserDeclined() {
    return localStorage.getItem(KEYS.userDeclined) === 'true';
  },

  daysSinceNativePrompt() {
    const ts = localStorage.getItem(KEYS.nativePromptShown);
    if (!ts) return null;
    return (Date.now() - parseInt(ts, 10)) / (1000 * 60 * 60 * 24);
  },

  async triggerNativePrompt() {
    if (!Capacitor.isNativePlatform()) return false;
    try {
      await Review.requestReview();
      localStorage.setItem(KEYS.nativePromptShown, String(Date.now()));
      return true;
    } catch (e) {
      console.warn('Native review request failed:', e);
      return false;
    }
  },

  async openAppStoreForReview() {
    try {
      await Review.openAppStoreReview({ appId: APP_STORE_ID });
      localStorage.setItem(KEYS.fallbackShown, 'true');
      return true;
    } catch (e) {
      console.warn('Open App Store failed:', e);
      return false;
    }
  },

  // Called the moment the custom pop-up appears, so it never shows twice
  // (tapping outside it counts as seen too).
  markPopupShown() {
    localStorage.setItem(KEYS.fallbackShown, 'true');
    localStorage.setItem(KEYS.reaskShown, 'true');
  },

  checkReask(opens) {
    if (localStorage.getItem(KEYS.reaskShown) === 'true') return null;
    const nativeTs = parseInt(localStorage.getItem(KEYS.nativePromptShown) || '0', 10);
    if (!nativeTs || nativeTs >= REASK_CUTOFF) return null; // new users follow the normal flow
    const start = localStorage.getItem(KEYS.reaskStart);
    if (start === null) {
      // First open after the update: stay out of the way, ask next time
      localStorage.setItem(KEYS.reaskStart, String(opens));
      return null;
    }
    if (opens <= parseInt(start, 10)) return null;
    if (new Date().getDate() <= 7) return null; // month-end review pop-up week
    return 'fallback';
  },

  markUserDeclined() {
    localStorage.setItem(KEYS.userDeclined, 'true');
    localStorage.setItem(KEYS.fallbackShown, 'true');
  },

  // Returns: 'native' | 'fallback' | null
  checkTrigger() {
    if (!Capacitor.isNativePlatform()) return null;

    const opens = this.getOpenCount();

    const reask = this.checkReask(opens);
    if (reask) return reask;

    if (this.hasUserDeclined()) return null;

    if (opens >= 3 && !this.hasShownNativePrompt()) return 'native';

    if (this.hasShownNativePrompt() && !this.hasShownFallback()) {
      const days = this.daysSinceNativePrompt();
      if (days !== null && days >= 14) return 'fallback';
    }

    return null;
  },
};
